import {
  collection,
  collectionGroup,
  query,
  where,
  orderBy,
  limit,
  getDocs,
  doc,
  updateDoc,
  deleteDoc,
  addDoc,
  onSnapshot,
  QuerySnapshot,
  writeBatch,
  type QueryConstraint,
  type QueryDocumentSnapshot
} from 'firebase/firestore'
import { db as sharedDb, auth, getActiveAuth, getActiveDb } from '../config/firebase'
import { transformKeysToCamel, transformKeysToSnake } from '../utils/namingConventions'
import type { Notification, NotificationFilters, NotificationCounts } from '../types/notifications'
import { NotificationType, NotificationStatus, NotificationPriority } from '../types/notifications'
import { debugFirebaseConnection, FirestoreConnectionManager } from '../config/firebase'

export class NotificationsService {
  // Use the active Firestore instance to support view-as functionality
  private getDb = () => getActiveDb()
  private readonly COLLECTION_NAME = 'notifications'
  private readonly PROFILES_COLLECTION = 'profiles'
  private static envChecked = false
  
  /**
   * Wait for Firebase Auth to be fully ready and synchronized with Firestore
   * This prevents race conditions where auth state changes but Firestore SDK hasn't received the token yet
   */
  private async waitForAuthSync(): Promise<boolean> {
    // Use the active auth instance
    
    // Wait for auth state to be ready
    try {
      await getActiveAuth().authStateReady()
    } catch (error) {
      console.error('NotificationsService: Auth state ready failed:', error)
      return false
    }
    
    const currentUser = getActiveAuth().currentUser
    if (!currentUser) {
      return false
    }
    
    // Force fresh token (not cached) to ensure Firestore SDK gets latest token
    try {
      await currentUser.getIdToken(true) // true = force refresh
      // Extended delay to ensure Firestore SDK syncs the fresh token
      // This prevents "false for 'list'" permission errors during initial subscription setup
      await new Promise(resolve => setTimeout(resolve, 1000))
      return true
    } catch (error) {
      console.error('NotificationsService: Failed to get fresh ID token:', error)
      return false
    }
  }
  
  /**
   * Get notifications for a user across all profiles
   */
  async getUserNotifications(
    userId: string, 
    filters: NotificationFilters = {},
    limitCount: number = 50
  ): Promise<Notification[]> {
    try {
      // Wait for auth to be fully synchronized with Firestore
      const authReady = await this.waitForAuthSync()
      if (!authReady) {
        console.warn('NotificationsService: Auth not ready, returning empty notifications')
        return []
      }
      
      // Verify user ID matches
      const currentUser = getActiveAuth().currentUser
      if (!currentUser || currentUser.uid !== userId) {
        console.warn('NotificationsService: User ID mismatch, returning empty notifications')
        return []
      }
      
      const constraints = this.buildNotificationQueryConstraints(filters, limitCount)

      const notifications = await this.runNotificationQueries(userId, constraints)
      
      return notifications
    } catch (error) {
      // Check if this is a Firestore permission error during setup
      if (error instanceof Error && error.message.includes("false for 'list'")) {
        console.warn('NotificationsService: Firestore permission error during setup, returning empty notifications:', error.message);
        if (!NotificationsService.envChecked) {
          NotificationsService.envChecked = true
          Promise.resolve(debugFirebaseConnection()).catch(() => {})
        }
        return [];
      }
      console.error('Error fetching user notifications:', error)
      return []
    }
  }

  /**
   * Get notifications for a specific profile
   */
  async getProfileNotifications(
    profileId: string, 
    filters: NotificationFilters = {},
    limitCount: number = 50
  ): Promise<Notification[]> {
    try {
      const db = this.getDb()

      let q = query(
        collection(db, this.PROFILES_COLLECTION, profileId, this.COLLECTION_NAME),
        orderBy('created_at', 'desc'),
        limit(limitCount)
      )

      if (filters.types && filters.types.length > 0) {
        q = query(q, where('type', 'in', filters.types))
      }

      if (filters.status && filters.status.length > 0) {
        q = query(q, where('status', 'in', filters.status))
      }

      if (filters.read !== undefined) {
        q = query(q, where('read_at', filters.read ? '!=' : '==', null))
      }

      const snapshot = await getDocs(q)
      return this.transformNotifications(snapshot)
    } catch (error) {
      console.error('Error fetching profile notifications:', error)
      return []
    }
  }

  /**
   * Get notification counts for a user
   */
  async getNotificationCounts(userId: string): Promise<NotificationCounts> {
    try {
      // Wait for auth to be fully synchronized with Firestore
      const authReady = await this.waitForAuthSync()
      if (!authReady) {
        console.warn('NotificationsService: Auth not ready, returning zero counts')
        return {
          total: 0,
          unread: 0,
          byType: {} as Record<NotificationType, number>,
          byPriority: {
            [NotificationPriority.LOW]: 0,
            [NotificationPriority.NORMAL]: 0,
            [NotificationPriority.HIGH]: 0,
            [NotificationPriority.URGENT]: 0
          }
        }
      }
      
      const constraints = this.buildNotificationQueryConstraints({}, undefined)
      const docs = await this.runNotificationDocQueries(userId, constraints)

      // Filter out docs that would fail transformation (no profileId/userId)
      // This ensures count matches what's actually displayed
      const validDocs = docs.filter(doc => {
        const data = doc.data()
        const camelCaseData = transformKeysToCamel(data)
        const parentPath = doc.ref.parent.parent?.path || ''
        const isAccountLevel = parentPath.includes('/accounts/')
        const parentProfileId = doc.ref.parent.parent?.id
        const profileId = isAccountLevel
          ? (camelCaseData.profileId || '')
          : (camelCaseData.profileId || parentProfileId)
        const docUserId = camelCaseData.userId || camelCaseData.userid || camelCaseData.user_id || (isAccountLevel ? parentProfileId : null)
        return profileId !== undefined && Boolean(docUserId)
      })

      const total = validDocs.length
      const unread = validDocs.filter(doc => !doc.data().read_at).length

      const byType: Record<NotificationType, number> = {} as Record<NotificationType, number>
      Object.values(NotificationType).forEach(type => {
        byType[type] = 0
      })

      const byPriority: Record<NotificationPriority, number> = {
        [NotificationPriority.LOW]: 0,
        [NotificationPriority.NORMAL]: 0,
        [NotificationPriority.HIGH]: 0,
        [NotificationPriority.URGENT]: 0
      }

      validDocs.forEach(snapshotDoc => {
        const data = snapshotDoc.data()
        const type = data.type as NotificationType | undefined
        const priority = (data.priority || NotificationPriority.NORMAL) as NotificationPriority
        if (type && byType[type] !== undefined) {
          byType[type]++
        }
        if (byPriority[priority] !== undefined) {
          byPriority[priority]++
        }
      })

      return { total, unread, byType, byPriority }
    } catch (error) {
      // Check if this is a Firestore permission error during setup
      if (error instanceof Error && error.message.includes("false for 'list'")) {
        console.warn('NotificationsService: Firestore permission error during setup, returning zero counts:', error.message);
        if (!NotificationsService.envChecked) {
          NotificationsService.envChecked = true
          Promise.resolve(debugFirebaseConnection()).catch(() => {})
        }
        return {
          total: 0,
          unread: 0,
          byType: {} as Record<NotificationType, number>,
          byPriority: {
            [NotificationPriority.LOW]: 0,
            [NotificationPriority.NORMAL]: 0,
            [NotificationPriority.HIGH]: 0,
            [NotificationPriority.URGENT]: 0
          }
        };
      }
      console.error('Error fetching notification counts:', error)
      return {
        total: 0,
        unread: 0,
        byType: {} as Record<NotificationType, number>,
        byPriority: {
          [NotificationPriority.LOW]: 0,
          [NotificationPriority.NORMAL]: 0,
          [NotificationPriority.HIGH]: 0,
          [NotificationPriority.URGENT]: 0
        }
      }
    }
  }

  /**
   * Mark a notification as read
   */
  async markAsRead(
    notificationId: string,
    profileId: string,
    isAccountLevel?: boolean,
    documentPath?: string
  ): Promise<void> {
    try {
      const notificationRef = this.resolveNotificationRef(
        notificationId,
        profileId,
        isAccountLevel,
        documentPath
      )
      
      // Use snake_case for Firestore
      await updateDoc(notificationRef, {
        read_at: new Date(),
        status: NotificationStatus.READ
      })
    } catch (error) {
      console.error('Error marking notification as read:', error)
      throw error
    }
  }

  /**
   * Mark multiple notifications as read
   */
  async markMultipleAsRead(
    refs: Array<{ id: string; profileId: string; isAccountLevel?: boolean; documentPath?: string }>
  ): Promise<void> {
    try {
      const batch = writeBatch(this.getDb())
      
      refs.forEach(({ id, profileId, isAccountLevel, documentPath }) => {
        const notificationRef = this.resolveNotificationRef(id, profileId, isAccountLevel, documentPath)
        
        batch.update(notificationRef, {
          read_at: new Date(),
          status: NotificationStatus.READ
        })
      })
      
      await batch.commit()
    } catch (error) {
      console.error('Error marking multiple notifications as read:', error)
      throw error
    }
  }

  /**
   * Mark all notifications as read for a user
   */
  async markAllAsRead(userId: string): Promise<void> {
    try {
      const notifications = await this.getUserNotifications(userId, { read: false })
      // Include all notifications, including account-level ones
      const unreadRefs = notifications.map(n => ({ 
        id: n.id, 
        profileId: n.profileId || '',
        isAccountLevel: n.isAccountLevel,
        documentPath: n.documentPath
      }))

      if (unreadRefs.length > 0) {
        await this.markMultipleAsRead(unreadRefs)
      }
    } catch (error) {
      console.error('Error marking all notifications as read:', error)
      throw error
    }
  }

  /**
   * Delete a notification
   */
  async deleteNotification(
    notificationId: string,
    profileId: string,
    isAccountLevel?: boolean,
    documentPath?: string
  ): Promise<void> {
    try {
      const notificationRef = this.resolveNotificationRef(
        notificationId,
        profileId,
        isAccountLevel,
        documentPath
      )
      
      await deleteDoc(notificationRef)
    } catch (error) {
      console.error('Error deleting notification:', error)
      throw error
    }
  }

  /**
   * Delete multiple notifications
   */
  async deleteMultipleNotifications(
    refs: Array<{ id: string; profileId: string; isAccountLevel?: boolean; documentPath?: string }>
  ): Promise<void> {
    try {
      const batch = writeBatch(this.getDb())
      
      refs.forEach(({ id, profileId, isAccountLevel, documentPath }) => {
        const notificationRef = this.resolveNotificationRef(id, profileId, isAccountLevel, documentPath)
        batch.delete(notificationRef)
      })
      
      await batch.commit()
    } catch (error) {
      console.error('Error deleting multiple notifications:', error)
      throw error
    }
  }

  /**
   * Delete all notifications for a user
   */
  async deleteAllNotifications(userId: string): Promise<void> {
    try {
      console.log(`[deleteAllNotifications] Starting deletion for user: ${userId}`)
      
      const notifications = await this.getUserNotifications(userId, {})
      console.log(`[deleteAllNotifications] Found ${notifications.length} notifications to delete`)
      
      // Include all notifications, including account-level ones
      const refs = notifications.map(n => ({ 
        id: n.id, 
        profileId: n.profileId || '',
        isAccountLevel: n.isAccountLevel,
        documentPath: n.documentPath
      }))
      
      console.log(`[deleteAllNotifications] Account-level: ${refs.filter(r => r.isAccountLevel).length}, Profile-level: ${refs.filter(r => !r.isAccountLevel).length}`)

      if (refs.length > 0) {
        // Delete in batches to avoid Firestore batch limits (500 operations per batch)
        const batchSize = 500
        for (let i = 0; i < refs.length; i += batchSize) {
          const batch = refs.slice(i, i + batchSize)
          console.log(`[deleteAllNotifications] Deleting batch ${Math.floor(i / batchSize) + 1} (${batch.length} notifications)`)
          await this.deleteMultipleNotifications(batch)
        }
        console.log(`[deleteAllNotifications] Successfully deleted all notifications`)
      } else {
        console.log(`[deleteAllNotifications] No notifications to delete`)
      }
    } catch (error) {
      console.error('[deleteAllNotifications] Error deleting all notifications:', error)
      throw error
    }
  }

  /**
   * Create a new notification
   */
  async createNotification(notification: Omit<Notification, 'id' | 'createdAt' | 'status'>): Promise<string> {
    try {
      if (!notification.profileId) {
        throw new Error('profileId is required to create a notification')
      }

      const notificationData = {
        ...notification,
        createdAt: new Date(),
        status: NotificationStatus.PENDING
      }

      // Convert to snake_case for Firestore
      const firestoreData = transformKeysToSnake(notificationData)

       const docRef = await addDoc(
        collection(this.getDb(), this.PROFILES_COLLECTION, notification.profileId, this.COLLECTION_NAME),
        firestoreData
      )
      return docRef.id
    } catch (error) {
      console.error('Error creating notification:', error)
      throw error
    }
  }

  /**
   * Subscribe to real-time notification updates
   */
  subscribeToNotifications(
    userId: string,
    callback: (notifications: Notification[]) => void,
    filters: NotificationFilters = {}
  ): () => void {
    // Set up subscription asynchronously after auth is ready
    let unsubscribe: (() => void) | null = null
    let retryCount = 0
    const maxRetries = 3
    
    const setupSubscription = async () => {
      const authReady = await this.waitForAuthSync()
      if (!authReady) {
        if (retryCount < maxRetries) {
          retryCount++
          console.warn(`NotificationsService: Auth not ready, retrying in ${retryCount * 1000}ms (attempt ${retryCount}/${maxRetries})`)
          setTimeout(setupSubscription, retryCount * 1000) // Exponential backoff
          return
        }
        console.warn('NotificationsService: Max retries reached, skipping subscription')
        return
      }
      
      const currentUser = getActiveAuth().currentUser
      if (!currentUser || currentUser.uid !== userId) {
        console.warn('NotificationsService: User ID mismatch, skipping subscription')
        return
      }
      
      // Verify token is available before attempting subscription
      try {
        const token = await currentUser.getIdToken()
        if (!token) {
          console.warn('NotificationsService: No auth token available, skipping subscription')
          return
        }
      } catch (error) {
        console.warn('NotificationsService: Failed to verify auth token:', error)
        if (retryCount < maxRetries) {
          retryCount++
          setTimeout(setupSubscription, retryCount * 1000)
          return
        }
        return
      }

      const constraints = this.buildNotificationQueryConstraints(filters, 50)

      const aggregated = new Map<string, Notification>()

      const emitAggregated = () => {
        const sorted = Array.from(aggregated.values()).sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime())
        callback(sorted)
      }

      const applySnapshot = (snapshot: QuerySnapshot<any>) => {
        snapshot.docChanges().forEach(change => {
          const mapped = this.mapNotificationDocument(change.doc)
          if (!mapped) {
            if (change.type === 'removed') {
              aggregated.delete(change.doc.ref.path)
            }
            return
          }

          const key = mapped.documentPath

          if (change.type === 'removed') {
            aggregated.delete(key)
            return
          }

          aggregated.set(key, mapped)
        })

        emitAggregated()
      }

      const handleError = (error: any) => {
        // Handle permission errors (including "Missing or insufficient permissions")
        // Firebase errors have a 'code' property that TypeScript's Error type doesn't include
        const errorCode = (error as any)?.code
        const isPermissionError = error instanceof Error && (
          error.message.includes("false for 'list'") ||
          error.message.includes("Missing or insufficient permissions") ||
          error.message.includes("permission-denied") ||
          (errorCode && (errorCode === 'permission-denied' || errorCode === 7))
        )
        
        if (isPermissionError) {
          console.warn('NotificationsService: Firestore permission error during subscription:', error.message || errorCode)
          if (!NotificationsService.envChecked) {
            NotificationsService.envChecked = true
            Promise.resolve(debugFirebaseConnection()).catch(() => {})
          }
          // Retry the subscription after a delay if we haven't exceeded max retries
          if (retryCount < maxRetries) {
            retryCount++
            console.log(`NotificationsService: Retrying subscription after permission error (attempt ${retryCount}/${maxRetries})`)
            setTimeout(() => {
              // Unsubscribe existing listeners before retrying
              if (unsubscribe) {
                unsubscribe()
                unsubscribe = null
              }
              setupSubscription().catch(retryError => {
                console.error('NotificationsService: Retry failed:', retryError)
              })
            }, retryCount * 2000) // Exponential backoff: 2s, 4s, 6s
          } else {
            console.warn('NotificationsService: Max retries reached for permission error, subscription will not be active')
          }
          return
        }
        // Handle Firestore Listen channel 400 errors with retry logic
        if (error instanceof Error && error.message.includes('400')) {
          console.warn('NotificationsService: Firestore Listen channel 400 error detected:', error.message)
          
          // Check if circuit breaker is open
          if (FirestoreConnectionManager.isCircuitOpen()) {
            console.warn('NotificationsService: Circuit breaker open, skipping retry')
            return
          }
          
          // Retry the subscription setup with exponential backoff
          setTimeout(() => {
            if (!FirestoreConnectionManager.isCircuitOpen()) {
              console.log('NotificationsService: Retrying subscription setup after Listen channel error')
              setupSubscription().catch(retryError => {
                console.error('NotificationsService: Retry failed:', retryError)
              })
            }
          }, 2000 + Math.random() * 3000) // 2-5 second delay with jitter
          return
        }
        console.error('Error in notification subscription:', error)
      }

      // Listen to profile-level notifications via collection group
      const profileListeners = [
        onSnapshot(query(collectionGroup(this.getDb(), this.COLLECTION_NAME), where('user_id', '==', userId), ...constraints), applySnapshot, handleError),
        onSnapshot(query(collectionGroup(this.getDb(), this.COLLECTION_NAME), where('userId', '==', userId), ...constraints), applySnapshot, handleError)
      ]
      
      // Also listen to account-level notifications directly
      const accountListener = onSnapshot(
        query(collection(this.getDb(), 'accounts', userId, this.COLLECTION_NAME), ...constraints),
        applySnapshot,
        handleError
      )
      
      const listeners = [...profileListeners, accountListener]

      unsubscribe = () => {
        listeners.forEach(off => off())
      }
    }

    // Start the subscription setup
    setupSubscription().catch(error => {
      console.error('NotificationsService: Failed to set up subscription:', error)
    })

    // Return unsubscribe function that works even if subscription setup is delayed
    return () => {
      if (unsubscribe) {
        unsubscribe()
      }
    }
  }

  /**
   * Transform Firestore documents to Notification objects
   */
  private transformNotifications(snapshot: QuerySnapshot<any>): Notification[] {
    const notifications: Notification[] = []

    snapshot.docs.forEach(doc => {
      const mapped = this.mapNotificationDocument(doc)
      if (mapped) {
        notifications.push(mapped)
      }
    })

    notifications.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime())
    return notifications
  }

  private notificationDocRef(profileId: string, notificationId: string) {
    return doc(this.getDb(), this.PROFILES_COLLECTION, profileId, this.COLLECTION_NAME, notificationId)
  }

  private notificationRefFromPath(documentPath: string) {
    const segments = documentPath.split('/').filter(Boolean)
    if (segments.length < 2) {
      throw new Error(`Invalid notification path: ${documentPath}`)
    }
    const [first, ...rest] = segments
    return doc(this.getDb(), first, ...rest)
  }

  private resolveNotificationRef(
    notificationId: string,
    profileId: string,
    isAccountLevel?: boolean,
    documentPath?: string
  ) {
    if (documentPath) {
      return this.notificationRefFromPath(documentPath)
    }

    const currentUser = getActiveAuth().currentUser
    if (!currentUser) {
      throw new Error('User not authenticated')
    }

    const isAccountPath = isAccountLevel !== undefined
      ? isAccountLevel
      : (!profileId || profileId === '')

    if (isAccountPath) {
      return doc(
        this.getDb(),
        'accounts',
        currentUser.uid,
        this.COLLECTION_NAME,
        notificationId
      )
    }

    return this.notificationDocRef(profileId, notificationId)
  }

  private buildNotificationQueryConstraints(filters: NotificationFilters, limitCount?: number): QueryConstraint[] {
    const constraints: QueryConstraint[] = []

    if (filters.types && filters.types.length > 0) {
      constraints.push(where('type', 'in', filters.types))
    }

    if (filters.status && filters.status.length > 0) {
      constraints.push(where('status', 'in', filters.status))
    }

    if (filters.read !== undefined) {
      constraints.push(where('read_at', filters.read ? '!=' : '==', null))
    }

    if (filters.profileId) {
      constraints.push(where('profile_id', '==', filters.profileId))
    }

    constraints.push(orderBy('created_at', 'desc'))

    if (limitCount) {
      constraints.push(limit(limitCount))
    }

    return constraints
  }

  private async runNotificationQueries(userId: string, constraints: QueryConstraint[]): Promise<Notification[]> {
    // Query profile-level notifications via collection group
    const profileQueries = [
      query(collectionGroup(this.getDb(), this.COLLECTION_NAME), where('user_id', '==', userId), ...constraints),
      query(collectionGroup(this.getDb(), this.COLLECTION_NAME), where('userId', '==', userId), ...constraints)
    ]
    
    // Also query account-level notifications directly
    // For account-level, we use the same constraints (they don't include user_id/userId filters)
    // since we're querying directly under accounts/{userId}
    const accountQuery = query(
      collection(this.getDb(), 'accounts', userId, this.COLLECTION_NAME),
      ...constraints
    )
    
    const snapshots = await Promise.all([
      ...profileQueries.map(q => getDocs(q)),
      getDocs(accountQuery)
    ])

    const mergedNotifications = new Map<string, Notification>()

    snapshots.forEach(snapshot => {
      this.transformNotifications(snapshot).forEach(notification => {
        mergedNotifications.set(notification.documentPath, notification)
      })
    })

    return Array.from(mergedNotifications.values()).sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime())
  }

  private async runNotificationDocQueries(userId: string, constraints: QueryConstraint[]): Promise<QueryDocumentSnapshot[]> {
    // Query profile-level notifications via collection group
    const profileQueries = [
      query(collectionGroup(this.getDb(), this.COLLECTION_NAME), where('user_id', '==', userId), ...constraints),
      query(collectionGroup(this.getDb(), this.COLLECTION_NAME), where('userId', '==', userId), ...constraints)
    ]
    
    // Also query account-level notifications directly
    // For account-level, we use the same constraints (they don't include user_id/userId filters)
    // since we're querying directly under accounts/{userId}
    const accountQuery = query(
      collection(this.getDb(), 'accounts', userId, this.COLLECTION_NAME),
      ...constraints
    )
    
    const snapshots = await Promise.all([
      ...profileQueries.map(q => getDocs(q)),
      getDocs(accountQuery)
    ])

    const docMap = new Map<string, QueryDocumentSnapshot>()
    snapshots.forEach(snapshot => {
      snapshot.docs.forEach(doc => {
        docMap.set(doc.ref.path, doc)
      })
    })

    return Array.from(docMap.values())
  }

  private mapNotificationDocument(doc: QueryDocumentSnapshot<any>): Notification | null {
    const data = doc.data()
    const camelCaseData = transformKeysToCamel(data)

    // Check if this is an account-level notification (stored under accounts/{userId}/notifications)
    const parentPath = doc.ref.parent.parent?.path || ''
    const isAccountLevel = parentPath.includes('/accounts/')
    
    const parentId = doc.ref.parent.parent?.id
    const userId = camelCaseData.userId || camelCaseData.userid || camelCaseData.user_id || (isAccountLevel ? parentId : null)
    
    // For account-level notifications, profileId should be empty string
    // For profile-level notifications, profileId comes from the parent or data
    const profileId = isAccountLevel 
      ? (camelCaseData.profileId || '') // Empty for account-level
      : (camelCaseData.profileId || parentId)

    if (!userId) {
      return null
    }

    const toDateSafe = (v: any): Date | undefined => {
      if (!v) return undefined
      if (typeof v.toDate === 'function') return v.toDate()
      if (v instanceof Date) return v
      if (typeof v === 'string') {
        const d = new Date(v)
        return isNaN(d.getTime()) ? undefined : d
      }
      if (typeof v === 'object' && typeof v.seconds === 'number') {
        return new Date(v.seconds * 1000)
      }
      return undefined
    }

    const derivedMessage = camelCaseData.message || (() => {
      switch (camelCaseData.type) {
        case 'access_request':
          return 'You have a new profile access request.'
        case 'profile_shared':
          return 'A profile has been shared with you.'
        default:
          return ''
      }
    })()

    const derivedActionText = camelCaseData.actionText || (camelCaseData.actionUrl ? 'Open' : undefined)

    return {
      id: doc.id,
      userId,
      profileId,
      documentPath: doc.ref.path,
      isAccountLevel,
      type: camelCaseData.type,
      title: camelCaseData.title,
      message: derivedMessage,
      data: camelCaseData.data,
      status: camelCaseData.status,
      priority: camelCaseData.priority,
      createdAt: toDateSafe(camelCaseData.createdAt) || new Date(),
      readAt: toDateSafe(camelCaseData.readAt),
      expiresAt: toDateSafe(camelCaseData.expiresAt),
      actionUrl: camelCaseData.actionUrl,
      actionText: derivedActionText
    }
  }
}

export const notificationsService = new NotificationsService()
